<template>
  <div style="margin:20px">
    <FiPageRail title="Data Sync — Vehicles" nowrap-actions>
      <template slot="actions">
        <b-button size="sm" variant="outline-secondary" :disabled="loading" @click.prevent="load_data()"
                  aria-label="Refresh" title="Re-run the comparison">
          <i class="fa fa-refresh" aria-hidden="true"></i>
        </b-button>
      </template>
    </FiPageRail>

    <div v-if="loading" style="text-align:center">
      <br/><br/><b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="!tbn_available" class="ds-panel"><div class="ds-empty">The TBN vehicle list could not be read right now.</div></div>

    <div v-else>
      <div class="ds-summary">
        <span class="stat-chip"><b>{{ total_active }}</b> differences</span>
      </div>

      <ds-section title="No telematics link"
                  :count="active_count('no_telematics_link', no_telematics_link, vkey)"
                  :excluded="excl_count('no_telematics_link', no_telematics_link, vkey)"
                  :open.sync="open.nolink" empty="Every TBN vehicle is linked to a telematics unit.">
        <table class="ds-table">
          <tr><th>Vehicle</th><th>Yard</th><th>VIN</th><th width="70px"></th></tr>
          <tr v-for="m in no_telematics_link" :key="'nl-' + m.vehicle" :class="{'ds-row-excluded': is_excluded('no_telematics_link', m.vehicle)}">
            <td><b>{{ m.vehicle }}</b></td><td>{{ m.yard || '&mdash;' }}</td><td>{{ m.vin || '&mdash;' }}</td>
            <td align="center"><a class="ds-exclude-btn" :title="xtitle('no_telematics_link', m.vehicle)"
                                  @click="toggle_item('no_telematics_link', m.vehicle)">{{ xlabel('no_telematics_link', m.vehicle) }}</a></td>
          </tr>
        </table>
      </ds-section>

      <ds-section title="In telematics, not in TBN"
                  :count="active_count('in_telematics_not_tbn', in_telematics_not_tbn, vkey)"
                  :excluded="excl_count('in_telematics_not_tbn', in_telematics_not_tbn, vkey)"
                  :open.sync="open.intele" :unavailable="in_telematics_not_tbn === null"
                  unavailable-text="No telematics roster." empty="Every telematics vehicle exists in TBN.">
        <table class="ds-table">
          <tr><th>Vehicle</th><th>Telematics VIN</th><th width="70px"></th></tr>
          <tr v-for="m in in_telematics_not_tbn" :key="'it-' + m.vehicle" :class="{'ds-row-excluded': is_excluded('in_telematics_not_tbn', m.vehicle)}">
            <td><b>{{ m.vehicle }}</b></td><td>{{ m.vin || '&mdash;' }}</td>
            <td align="center"><a class="ds-exclude-btn" :title="xtitle('in_telematics_not_tbn', m.vehicle)"
                                  @click="toggle_item('in_telematics_not_tbn', m.vehicle)">{{ xlabel('in_telematics_not_tbn', m.vehicle) }}</a></td>
          </tr>
        </table>
      </ds-section>

      <ds-section title="VIN mismatches"
                  :count="active_count('vin_mismatch', vin_mismatch, vkey)"
                  :excluded="excl_count('vin_mismatch', vin_mismatch, vkey)"
                  :open.sync="open.vin" :unavailable="vin_mismatch === null"
                  unavailable-text="No telematics roster." empty="VINs agree across systems.">
        <table class="ds-table">
          <tr><th>Vehicle</th><th>TBN VIN</th><th>Telematics VIN</th><th width="70px"></th></tr>
          <tr v-for="m in vin_mismatch" :key="'vin-' + m.vehicle" :class="{'ds-row-excluded': is_excluded('vin_mismatch', m.vehicle)}">
            <td><b>{{ m.vehicle }}</b></td><td>{{ m.tbn_vin }}</td><td>{{ m.telematics_vin }}</td>
            <td align="center"><a class="ds-exclude-btn" :title="xtitle('vin_mismatch', m.vehicle)"
                                  @click="toggle_item('vin_mismatch', m.vehicle)">{{ xlabel('vin_mismatch', m.vehicle) }}</a></td>
          </tr>
        </table>
      </ds-section>

      <ds-section v-if="fleetio" title="TBN vs Fleetio" :count="fleetio_active" :excluded="fleetio_excluded"
                  :open.sync="open.fleetio" empty="Every vehicle matches between TBN and Fleetio.">
        <div v-if="fleetio.in_fleetio_not_tbn.length" class="ds-cat-label">In Fleetio, not in TBN</div>
        <table v-if="fleetio.in_fleetio_not_tbn.length" class="ds-table">
          <tr><th>Vehicle</th><th>Fleetio group</th><th width="70px"></th></tr>
          <tr v-for="m in fleetio.in_fleetio_not_tbn" :key="'fo-' + m.vehicle" :class="{'ds-row-excluded': is_excluded('fleetio_only', m.vehicle)}">
            <td><b>{{ m.vehicle }}</b></td><td>{{ m.group || '&mdash;' }}</td>
            <td align="center"><a class="ds-exclude-btn" :title="xtitle('fleetio_only', m.vehicle)"
                                  @click="toggle_item('fleetio_only', m.vehicle)">{{ xlabel('fleetio_only', m.vehicle) }}</a></td>
          </tr>
        </table>
        <div v-if="fleetio.in_tbn_not_fleetio.length" class="ds-cat-label">In TBN, not in Fleetio</div>
        <table v-if="fleetio.in_tbn_not_fleetio.length" class="ds-table">
          <tr><th>Vehicle</th><th>Home yard (TBN)</th><th width="70px"></th></tr>
          <tr v-for="m in fleetio.in_tbn_not_fleetio" :key="'tf-' + m.vehicle" :class="{'ds-row-excluded': is_excluded('tbn_not_fleetio', m.vehicle)}">
            <td><b>{{ m.vehicle }}</b></td><td>{{ m.yard || '&mdash;' }}</td>
            <td align="center"><a class="ds-exclude-btn" :title="xtitle('tbn_not_fleetio', m.vehicle)"
                                  @click="toggle_item('tbn_not_fleetio', m.vehicle)">{{ xlabel('tbn_not_fleetio', m.vehicle) }}</a></td>
          </tr>
        </table>
      </ds-section>
    </div>
  </div>
</template>

<script>
import {get_data_sync_vehicles} from '../networking/tbn_apis'
import {mapState} from 'vuex'
import DsSection from './data_sync_section'
import {dataSyncExclusions} from '../mixins/data_sync_exclusions'

export default {
  name: 'data_sync_vehicles',
  components: {DsSection},
  mixins: [dataSyncExclusions('vehicles')],
  data() {
    return {
      loading: false,
      tbn_available: true,
      no_telematics_link: null,
      in_telematics_not_tbn: null,
      vin_mismatch: null,
      fleetio: null,
      exclusions: {},
      open: {nolink: false, intele: false, vin: false, fleetio: false},
    }
  },
  created() { this.load_data() },
  computed: {
    ...mapState({user: state => state.profile.user}),
    fleetio_active() {
      return this.fleetio ? this.active_count('fleetio_only', this.fleetio.in_fleetio_not_tbn, this.vkey)
        + this.active_count('tbn_not_fleetio', this.fleetio.in_tbn_not_fleetio, this.vkey) : 0
    },
    fleetio_excluded() {
      return this.fleetio ? this.excl_count('fleetio_only', this.fleetio.in_fleetio_not_tbn, this.vkey)
        + this.excl_count('tbn_not_fleetio', this.fleetio.in_tbn_not_fleetio, this.vkey) : 0
    },
    total_active() {
      return this.active_count('no_telematics_link', this.no_telematics_link, this.vkey)
        + this.active_count('in_telematics_not_tbn', this.in_telematics_not_tbn, this.vkey)
        + this.active_count('vin_mismatch', this.vin_mismatch, this.vkey)
        + this.fleetio_active
    },
  },
  watch: {
    'user.tenant'(n, o) { if (o && n !== o) this.load_data() },
  },
  methods: {
    vkey(m) { return m.vehicle },  // the key every vehicle row is excluded on
    xtitle(cat, key) { return this.is_excluded(cat, key) ? 'Restore' : 'Exclude permanently' },
    xlabel(cat, key) { return this.is_excluded(cat, key) ? 'Restore' : 'Exclude' },
    async load_data(silent = false) {
      if (!silent) this.loading = true
      try {
        const {value: {data = {}}} = await get_data_sync_vehicles(this.user.tenant)
        this.tbn_available = data.tbn_available !== false
        this.no_telematics_link = data.no_telematics_link === undefined ? null : data.no_telematics_link
        this.in_telematics_not_tbn = data.in_telematics_not_tbn === undefined ? null : data.in_telematics_not_tbn
        this.vin_mismatch = data.vin_mismatch === undefined ? null : data.vin_mismatch
        this.fleetio = data.fleetio === undefined ? null : data.fleetio
        this.exclusions = data.exclusions || {}
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data, try again or contact support', 'danger', 5000)
      }
      if (!silent) this.loading = false
    },
  },
}
</script>

<style scoped>
@import './data_sync.css';
</style>
